import React, { useState } from 'react';
import { X, Palette, MessageSquare, User, Mail, MessageCircle } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth } from '../services/firebase';
import { colorOptions } from './noteStyles';

const NavDrawer = ({ isOpen, onClose, user, userPreferences, updateUserPreferences }) => {
  const [feedbackType, setFeedbackType] = useState('bug');
  const [feedbackContent, setFeedbackContent] = useState('');
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (isSigningOut) return;

    try {
      setIsSigningOut(true);
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
      setIsSigningOut(false);
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const updateSetting = async (key, value) => {
    const newPreferences = { ...userPreferences, [key]: value };
    updateUserPreferences(newPreferences);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const db = getFirestore();
      const feedbackRef = collection(db, 'feedback');
      await addDoc(feedbackRef, {
        type: feedbackType,
        content: feedbackContent,
        userId: user.uid,
        userEmail: user.email,
        createdAt: serverTimestamp()
      });

      setFeedbackType('bug');
      setFeedbackContent('');
      setIsFeedbackSubmitted(true);
      setTimeout(() => setIsFeedbackSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    // Wrapper div to prevent click propagation
    <div className="fixed inset-0 isolate" style={{ zIndex: 9999 }}>
      {/* Semi-transparent overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={handleOverlayClick}
      />

      {/* Drawer content */}
      <div
        className="fixed inset-y-0 left-0 w-80 bg-white dark:bg-gray-800 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-1 right-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X size={24} />
        </button>

        {/* AisenHour Logo with white background */}
        <div className="bg-white p-4">
          <img
            src="/AisenHour-Hourglass-Horizontal.png"
            alt="AisenHour Logo"
            className="w-full h-auto"
          />
        </div>

        <div className="p-4 overflow-y-auto h-[calc(100vh-180px)]">
          {/* Account Info Section */}
          {user && (
            <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 dark:text-white flex items-center">
                <User size={20} className="mr-2" />
                Account Info
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">{user.displayName}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Default Note Color */}
            <div>
              <label className="flex items-center mb-2 dark:text-white">
                <Palette size={20} className="mr-2" />
                Default Note Color
              </label>
              <div className="grid grid-cols-5 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.class}
                    className={`w-full h-8 rounded ${color.class} ${userPreferences.defaultNoteColor === color.class ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => updateSetting('defaultNoteColor', color.class)}
                  />
                ))}
              </div>
            </div>

            {/* Contact Us Section */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 dark:text-white flex items-center">
                <Mail size={20} className="mr-2" />
                Contact Us
              </h3>
              <div className="space-y-2">
                <a
                  href="https://discord.com/channels/1296184261073834097/1296184370540974110"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <MessageCircle size={16} className="mr-2" />
                  Join our Discord Server
                </a>
                <a
                  href="mailto:support@aisenhour.com"
                  className="flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <Mail size={16} className="mr-2" />
                  support@aisenhour.com
                </a>
              </div>
            </div>

            {/* Feedback Form */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 dark:text-white flex items-center">
                <MessageSquare size={20} className="mr-2" />
                Beta Feedback
              </h3>
              {isFeedbackSubmitted ? (
                <p className="text-green-600 dark:text-green-400">Thank you for your feedback!</p>
              ) : (
                <form onSubmit={handleFeedbackSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Feedback Type
                    </label>
                    <select
                      value={feedbackType}
                      onChange={(e) => setFeedbackType(e.target.value)}
                      className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="bug">Bug Report</option>
                      <option value="feature">Feature Request</option>
                      <option value="other">Other Feedback</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Your Feedback
                    </label>
                    <textarea
                      value={feedbackContent}
                      onChange={(e) => setFeedbackContent(e.target.value)}
                      className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      rows="4"
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors duration-200"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </form>
              )}
            </div>
          </div>

          <button
            onClick={handleSignOut}
            disabled={isSigningOut}
            className={`w-full ${isSigningOut ? 'bg-gray-500' : 'bg-red-500 hover:bg-red-600'
              } text-white py-2 px-4 rounded transition-colors duration-200 mt-8`}
          >
            {isSigningOut ? 'Signing Out...' : 'Sign Out'}
          </button>
        </div>
      </div>
    </div>
  );
};
export default NavDrawer;